import { Component, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { useThemeColors } from 'hooks/useThemeColors'
import theme from 'theme'

interface FallbackProps {
  variant: 'card' | 'main'
}

// Functional so it can resolve theme tokens via hooks; the class boundary can't.
export const InsightErrorFallback = ({ variant }: FallbackProps) => {
  const getColor = useThemeColors()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${theme.spacing.small}px`,
        textAlign: 'center',
        width: '100%',
        height: variant === 'main' ? '100%' : 'auto',
        minHeight: variant === 'card' ? '96px' : '240px',
        padding: `${theme.spacing.large}px`,
        boxSizing: 'border-box',
        border: `1px solid ${getColor('stroke-primary')}`,
        borderRadius: `${theme.attributes.borderRadius.secondary}`,
        background: getColor('color-surfaceContainerBackground')
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily.primary,
          fontSize: theme.typography.fontSize.mediumSmall,
          fontWeight: theme.typography.fontWeight.bold,
          color: getColor('text-primary')
        }}
      >
        This insight couldn&apos;t be displayed
      </Typography>
      {variant === 'main' && (
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: theme.typography.fontSize.small,
            color: getColor('text-secondary')
          }}
        >
          Something went wrong rendering this insight. Try selecting another one.
        </Typography>
      )}
    </Box>
  )
}

interface Props {
  children: ReactNode
  fallback: ReactNode
  // Changing this clears the error state so a healthy insight recovers after a throw.
  resetKey?: unknown
}

interface State {
  hasError: boolean
}

class InsightErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) return this.props.fallback
    return this.props.children
  }
}

export default InsightErrorBoundary
